import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { fetchMyContent, publishContent, deleteContent } from '../store/contentSlice';
import { loadUser } from '../store/authSlice';
import ConfirmModal from '../components/ConfirmModal';
import { formatCount } from '../utils/dates';
import { getThumbnailUrl } from '../utils/media';
import { useThumbnailAuth } from '../hooks/useThumbnailAuth';

const TYPE_LABELS = {
  movie: 'Movies', series: 'Series', documentary: 'Documentaries', interview: 'Interviews',
  course: 'Courses', mentorship: 'Mentorships', news: 'News', class: 'Classes', other: 'Other'
};

export default function CreatorPanel() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { myContent, loading } = useSelector((state) => state.content);
  const thumbAuth = useThumbnailAuth();
  const [publishingId, setPublishingId] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    dispatch(fetchMyContent());
  }, [dispatch]);

  const totalPages = Math.ceil(myContent.length / perPage);
  const paginated = myContent.slice((page - 1) * perPage, page * perPage);

  const handlePublish = async (id) => {
    setPublishingId(id);
    setPublishError(null);
    const result = await dispatch(publishContent(id));
    if (result.error) {
      setPublishError(result.payload || 'Error publishing content');
    }
    setPublishingId(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const result = await dispatch(deleteContent(deleteTarget.id));
    if (!result.error) {
      dispatch(loadUser());
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <div className="creator-page">
      <div className="creator-header">
        <h2>My Creator Panel</h2>
        <button onClick={() => navigate('/upload')} className="btn creator-add-btn">
          + New Content
        </button>
      </div>

      {publishError && <div className="error-msg">{publishError}</div>}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : myContent.length === 0 ? (
        <div className="empty">No content yet</div>
      ) : (
        <>
          <table className="creator-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Price</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((c) => (
                <tr key={c.id}>
                  <td><Link to={`/content/${c.id}`} style={{ color: '#C8963E', textDecoration: 'underline' }}>{c.title}</Link></td>
                  <td>{c.type}</td>
                  <td>{parseFloat(parseFloat(c.price_usdc).toFixed(2))} $</td>
                  <td><span className={c.is_published ? 'status-confirmed' : 'status-pending'}>{c.is_published ? 'Published' : 'Draft'}</span></td>
                  <td className="creator-actions">
                    {!c.is_published && (
                      <button onClick={() => handlePublish(c.id)} className="btn btn-small upload-submit-btn" disabled={publishingId === c.id}>
                        {publishingId === c.id ? (
                          <span className="upload-btn-clock">
                            <span className="upload-btn-clock-ring" />
                            <span className="upload-btn-clock-hand" />
                            <span className="upload-btn-clock-dot" />
                          </span>
                        ) : 'Publish'}
                      </button>
                    )}
                    <button onClick={() => setDeleteTarget({ id: c.id, title: c.title })} className="btn btn-small btn-danger">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="creator-pagination">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="creator-page-btn">&laquo; Previous</button>
              <span className="creator-page-info">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="creator-page-btn">Next &raquo;</button>
            </div>
          )}
        </>
      )}

      {/* ===== UPLOADED VIDEOS GRID ===== */}
      {myContent.length > 0 && (
        <div className="creator-videos-section">
          <h3>Uploaded Videos</h3>
          <div className="content-grid creator-videos-grid">
            {myContent.map((c) => {
              const thumb = getThumbnailUrl(c.media_url, c.thumbnail_key, thumbAuth);
              return (
                <Link to={`/content/${c.id}`} key={c.id} className="content-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="content-card-thumb">
                    {thumb ? (
                      <img src={thumb} alt={c.title} loading="lazy" />
                    ) : (
                      <div className="content-card-placeholder">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <polygon points="5,3 19,12 5,21" />
                        </svg>
                        <span>No thumbnail</span>
                      </div>
                    )}
                    {!c.is_published && <span className="creator-draft-badge">Draft</span>}
                  </div>
                  <div className="content-card-info">
                    <h3>{c.title}</h3>
                    <div className="content-card-meta">
                      <span className="content-card-creator">{formatCount(c.view_count || 0)} views</span>
                      <span className="content-card-price">{parseFloat(parseFloat(c.price_usdc).toFixed(2))} $</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title={`Delete "${deleteTarget.title}"?`}
          message="This will permanently remove the video and all associated data."
          confirmLabel={deleting ? null : 'Delete'}
          confirmIcon={deleting ? (
            <span className="upload-btn-clock">
              <span className="upload-btn-clock-ring" />
              <span className="upload-btn-clock-hand" />
              <span className="upload-btn-clock-dot" />
            </span>
          ) : null}
          disabled={deleting}
          danger
          onConfirm={confirmDelete}
          onCancel={deleting ? undefined : () => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
